/**
 * Typed client for the card-merge engine.
 *
 * The gateway creates a NEW consolidated card, moves/re-parents the source
 * content into it, and archives the source cards as read-only tombstones.
 * Every write is server-authoritative and org/scope fenced.
 */
import { ApiError, apiJson } from "./client";

export interface CardMergeTargetInput {
  projectId?: string;
  boardId?: string;
  columnId?: string;
  title?: string;
  description?: string;
  cardInstructions?: string;
  assigneeKind?: "user" | "role" | "agent_instance" | null;
  assigneeUserId?: string | null;
  assigneeRoleId?: string | null;
  roleId?: string | null;
}

export interface CardMergeOptions {
  exportConversations?: boolean;
  copyVoice?: boolean;
  includeArchivedFiles?: boolean;
}

export interface CardMergeRequest {
  sourceCardIds: string[];
  target?: CardMergeTargetInput;
  options?: CardMergeOptions;
}

export interface CardMergeBlocker {
  kind: string;
  cardId?: string;
  detail?: string;
}

export interface CardMergeAccessEntry {
  principalKind: string;
  principalId: string;
  via: string;
}

export interface CardMergePreview {
  canMerge: boolean;
  orgId: string;
  blockers: CardMergeBlocker[];
  accessUnion: CardMergeAccessEntry[];
  plan: {
    sources: Array<{
      cardId: string;
      ordinal: number;
      title: string;
      boardId: string | null;
      folderSlug: string;
    }>;
    target: {
      title: string;
      projectId: string;
      boardId: string | null;
      columnId: string;
      priority: string;
      description: string;
      cardInstructions: string;
    };
  };
}

export interface CardMergeCreated {
  mergeId: string;
  status: "pending";
}

export interface CardMergeStatus {
  mergeId: string;
  targetCardId: string;
  status: string;
  stats: Record<string, unknown>;
  error: string | null;
  createdAt: string;
  finishedAt: string | null;
}

export interface CardMergeSourceInfo {
  sourceCardId: string;
  ordinal: number;
  title: string;
  folderSlug: string;
  stats: Record<string, unknown> | null;
}

export type CardMergeInfo =
  | { merged: false }
  | {
      merged: true;
      isTombstone: boolean;
      tombstone: { mergedIntoCardId: string; mergedAt: string | null } | null;
      target: {
        mergeId: string;
        status: string;
        mergedAt: string;
        sources: CardMergeSourceInfo[];
        itemCountsByKind: Record<string, number>;
      } | null;
      generations: Array<{ mergeId: string; status: string; mergedAt: string }>;
    };

export const cardMergeApi = {
  preview(input: CardMergeRequest): Promise<CardMergePreview> {
    return apiJson<CardMergePreview>("/cards/merge-preview", {
      method: "POST",
      json: input,
    });
  },

  create(input: CardMergeRequest): Promise<CardMergeCreated> {
    return apiJson<CardMergeCreated>("/cards/merge", {
      method: "POST",
      json: input,
    });
  },

  getStatus(mergeId: string): Promise<CardMergeStatus> {
    return apiJson<CardMergeStatus>(`/card-merges/${mergeId}`);
  },

  getInfo(cardId: string): Promise<CardMergeInfo> {
    return apiJson<CardMergeInfo>(`/cards/${cardId}/merge-info`);
  },
};

export const cardMergeKeys = {
  all: ["card-merge"] as const,
  info: (cardId: string) => [...cardMergeKeys.all, "info", cardId] as const,
  status: (mergeId: string) =>
    [...cardMergeKeys.all, "status", mergeId] as const,
};

const BLOCKER_LABELS: Record<string, string> = {
  live_run: "An agent is still running on this card.",
  live_session: "An interactive session is still active on this card.",
  queued_launch: "This card has an agent launch waiting in the queue.",
  scheduled_task_running: "A scheduled task is currently running on this card.",
  subagent_running: "A sub-agent is still running on this card.",
  research_running: "A research task is still running on this card.",
  debate_running: "A debate is still running on this card.",
  call_active: "A call is still active on this card.",
  scheduled_call_active: "A scheduled call is still active on this card.",
  incompatible_access_grant_lifetime:
    "Temporary access grants on these cards use incompatible session lifetimes. Normalize them before merging.",
  concurrent_merge: "This card is already part of another merge.",
};

export function describeCardMergeBlocker(blocker: CardMergeBlocker): string {
  return (
    BLOCKER_LABELS[blocker.kind] ||
    blocker.detail ||
    "This card is not ready to merge yet."
  );
}

const ERROR_LABELS: Record<string, string> = {
  card_merge_disabled: "Card merge is temporarily unavailable.",
  need_two_sources: "Choose at least two distinct cards.",
  bad_request: "Check the selected cards and target details, then try again.",
  source_not_found: "One or more selected cards are no longer available.",
  source_is_tombstone: "One of the selected cards has already been merged.",
  source_in_trash: "Restore cards from Trash before merging them.",
  source_archived: "Restore archived cards before merging them.",
  source_orgless:
    "One of the selected cards is not attached to a Workspace.",
  cross_org_blocked: "Cards can only be merged inside one Workspace.",
  invalid_destination: "Choose a valid destination board and status.",
  preflight_blocked:
    "Finish or stop the active work on these cards before merging.",
  source_changed:
    "One of the source cards changed while the merge was being prepared. Review it and try again.",
  forbidden: "You do not have permission to merge these cards.",
  not_found: "This merge is no longer available.",
  merge_create_failed:
    "The merge could not be started. No source cards were changed.",
};

export function cardMergeErrorMessage(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return "Something went wrong. No source cards were changed.";
  }
  const friendly = ERROR_LABELS[error.code];
  if (friendly) return friendly;
  const body = error.detail as { message?: unknown } | undefined;
  if (typeof body?.message === "string" && body.message.trim())
    return body.message;
  return error.message || "The merge request failed.";
}

export function cardMergeErrorBlockers(error: unknown): CardMergeBlocker[] {
  if (!(error instanceof ApiError)) return [];
  const body = error.detail as { blockers?: unknown } | undefined;
  if (!Array.isArray(body?.blockers)) return [];
  return body.blockers.filter(
    (value): value is CardMergeBlocker =>
      !!value &&
      typeof value === "object" &&
      typeof (value as CardMergeBlocker).kind === "string",
  );
}
